import React, { useState, useEffect, useCallback } from 'react';
import { useApp } from '../context/AppContext';
import { transactionsApi } from '../utils/api';
import { formatRupee } from '../utils/format';

export default function AddTransaction({ navigate }) {
  const { stocks, fetchStocks, addStock } = useApp();
  const [form, setForm] = useState({
    stockId: '',
    quantity: '',
    price: '',
    purchaseDate: new Date().toISOString().split('T')[0],
    type: 'BUY',
    notes: ''
  });
  const [newStockName, setNewStockName] = useState('');
  const [mode, setMode] = useState('existing'); // 'existing' | 'new'
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => { fetchStocks(); }, [fetchStocks]);

  const totalValue = form.quantity && form.price
    ? parseFloat(form.quantity) * parseFloat(form.price)
    : 0;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      let stockId = form.stockId;

      // If new stock mode, create stock first
      if (mode === 'new') {
        if (!newStockName.trim()) {
          setError('Stock name is required');
          setLoading(false);
          return;
        }
        const res = await addStock({ stockName: newStockName.trim() });
        stockId = res.data._id;
      }

      if (!stockId) {
        setError('Please select or add a stock');
        setLoading(false);
        return;
      }

      if (!form.quantity || parseFloat(form.quantity) <= 0) {
        setError('Quantity must be greater than 0');
        setLoading(false);
        return;
      }
      if (!form.price || parseFloat(form.price) <= 0) {
        setError('Price must be greater than 0');
        setLoading(false);
        return;
      }

      await transactionsApi.create({
        ...form,
        stockId,
        quantity: parseFloat(form.quantity),
        price: parseFloat(form.price)
      });

      setSuccess(true);
      setForm({
        stockId: '',
        quantity: '',
        price: '',
        purchaseDate: new Date().toISOString().split('T')[0],
        type: 'BUY',
        notes: ''
      });
      setNewStockName('');
      fetchStocks();

      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to add transaction');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <div className="page-header">
        <h2>Add Transaction</h2>
        <p>Record a new stock purchase or sale in your ledger</p>
      </div>

      <div style={{ maxWidth: 620 }}>
        <div className="card">
          {success && (
            <div className="alert alert-success">
              ✓ Transaction recorded successfully!
            </div>
          )}
          {error && (
            <div className="alert alert-error">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit}>
            {/* Stock Selection */}
            <div className="form-group">
              <label>Stock</label>
              <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
                <button
                  type="button"
                  className={`btn ${mode === 'existing' ? 'btn-primary' : 'btn-outline'} btn-sm`}
                  onClick={() => setMode('existing')}
                >
                  Select Existing
                </button>
                <button
                  type="button"
                  className={`btn ${mode === 'new' ? 'btn-primary' : 'btn-outline'} btn-sm`}
                  onClick={() => setMode('new')}
                >
                  + New Stock
                </button>
              </div>

              {mode === 'existing' ? (
                <select
                  name="stockId"
                  value={form.stockId}
                  onChange={handleChange}
                  required
                >
                  <option value="">— Select a stock —</option>
                  {stocks.map(s => (
                    <option key={s._id} value={s._id}>
                      {s.stockName} {s.ticker ? `(${s.ticker})` : ''}
                    </option>
                  ))}
                </select>
              ) : (
                <input
                  type="text"
                  placeholder="e.g. RELIANCE, TCS, INFY…"
                  value={newStockName}
                  onChange={e => setNewStockName(e.target.value.toUpperCase())}
                  style={{ textTransform: 'uppercase' }}
                />
              )}
            </div>

            {/* Type */}
            <div className="form-group">
              <label>Transaction Type</label>
              <div style={{ display: 'flex', gap: 10 }}>
                {['BUY', 'SELL'].map(t => (
                  <button
                    key={t}
                    type="button"
                    onClick={() => setForm(f => ({ ...f, type: t }))}
                    style={{
                      flex: 1,
                      padding: '12px',
                      borderRadius: '8px',
                      border: '2px solid',
                      borderColor: form.type === t
                        ? (t === 'BUY' ? 'var(--green)' : 'var(--red)')
                        : 'var(--border)',
                      background: form.type === t
                        ? (t === 'BUY' ? 'var(--green-soft)' : 'var(--red-soft)')
                        : 'transparent',
                      color: form.type === t
                        ? (t === 'BUY' ? 'var(--green)' : 'var(--red)')
                        : 'var(--text-muted)',
                      fontFamily: 'var(--font)',
                      fontSize: '0.9rem',
                      fontWeight: 700,
                      cursor: 'pointer',
                      transition: 'var(--transition)'
                    }}
                  >
                    {t === 'BUY' ? '↑ BUY' : '↓ SELL'}
                  </button>
                ))}
              </div>
            </div>

            {/* Quantity & Price */}
            <div className="form-row form-row-2 form-group">
              <div>
                <label>Quantity</label>
                <input
                  type="number"
                  name="quantity"
                  placeholder="e.g. 10"
                  value={form.quantity}
                  onChange={handleChange}
                  min="0.001"
                  step="any"
                  required
                />
              </div>
              <div>
                <label>Price per Share (₹)</label>
                <div className="input-prefix">
                  <span>₹</span>
                  <input
                    type="number"
                    name="price"
                    placeholder="e.g. 2400"
                    value={form.price}
                    onChange={handleChange}
                    min="0.01"
                    step="any"
                    required
                  />
                </div>
              </div>
            </div>

            {/* Total Value Preview */}
            {totalValue > 0 && (
              <div style={{
                background: 'var(--accent-soft)',
                border: '1px solid var(--accent)',
                borderRadius: 'var(--radius-sm)',
                padding: '12px 16px',
                marginBottom: 20,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}>
                <span style={{ fontSize: '0.82rem', color: 'var(--text-muted)' }}>
                  Total {form.type} Value
                </span>
                <span style={{ fontFamily: 'var(--mono)', fontWeight: 700, color: 'var(--accent)', fontSize: '1.1rem' }}>
                  {formatRupee(totalValue)}
                </span>
              </div>
            )}

            {/* Date */}
            <div className="form-group">
              <label>Purchase Date</label>
              <input
                type="date"
                name="purchaseDate"
                value={form.purchaseDate}
                onChange={handleChange}
                max={new Date().toISOString().split('T')[0]}
                required
              />
            </div>

            {/* Notes */}
            <div className="form-group">
              <label>Notes <span style={{ color: 'var(--text-dim)', textTransform: 'none', fontWeight: 400 }}>(optional)</span></label>
              <input
                type="text"
                name="notes"
                placeholder="e.g. SIP purchase, averaged down…"
                value={form.notes}
                onChange={handleChange}
                maxLength={300}
              />
            </div>

            {/* Submit */}
            <div style={{ display: 'flex', gap: 12, marginTop: 8 }}>
              <button
                type="submit"
                className="btn btn-primary btn-lg"
                disabled={loading}
                style={{ flex: 1 }}
              >
                {loading ? 'Saving…' : `Record ${form.type} Transaction`}
              </button>
              <button
                type="button"
                className="btn btn-outline"
                onClick={() => navigate('ledger')}
              >
                View Ledger
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
